import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";

const TOTAL_STEPS = 7;
const STEP_TITLES = [
  "Business identity",
  "Compliance & credentials",
  "Service coverage",
  "Profile & gallery",
  "References",
  "Code of Conduct",
  "Choose your tier",
];

const AU_STATES = ["NSW", "VIC", "QLD", "WA", "SA", "TAS", "ACT", "NT"];
const BUSINESS_TYPES = [
  { value: "sole_trader", label: "Sole trader" },
  { value: "company", label: "Company" },
  { value: "partnership", label: "Partnership" },
  { value: "trust", label: "Trust" },
];
const JOB_TYPES = ["interior", "exterior", "commercial", "heritage", "specialist"];

const useDebouncedSave = (fn, delay = 800) => {
  const [t, setT] = useState(null);
  return (...args) => {
    if (t) clearTimeout(t);
    setT(setTimeout(() => fn(...args), delay));
  };
};

export default function MembershipApply() {
  const nav = useNavigate();
  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState(1);
  const [err, setErr] = useState("");
  const [submitErrors, setSubmitErrors] = useState([]);

  // Bootstrap
  useEffect(() => {
    (async () => {
      setErr("");
      try {
        const { data } = await api.post("/membership/application/me");
        setApp(data);
        setStep(data.current_step || 1);
      } catch (e) {
        setErr(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async (patch, { silent = false } = {}) => {
    if (!app) return;
    if (!silent) setSaving(true);
    try {
      const { data } = await api.patch("/membership/application/me", patch);
      setApp(data);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      if (!silent) setSaving(false);
    }
  };
  const debouncedSave = useDebouncedSave((patch) => save(patch, { silent: true }));

  const goToStep = async (target) => {
    if (target < 1 || target > TOTAL_STEPS) return;
    await save({ current_step: target });
    setStep(target);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async () => {
    setSubmitErrors([]);
    setErr("");
    setSaving(true);
    try {
      const { data } = await api.post("/membership/application/me/submit");
      setApp(data);
      nav("/painter/membership/apply/done");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && Array.isArray(detail.errors)) {
        setSubmitErrors(detail.errors);
        setErr(detail.message || "Application has unresolved errors.");
        setStep(detail.errors[0]?.step || step);
      } else {
        setErr(formatApiError(e));
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-8 text-brand-navy" data-testid="apply-loading">Loading your application…</div>;
  if (!app) return <div className="p-8 text-red-700" data-testid="apply-error">{err || "Unable to load application."}</div>;

  // draft + info_requested show the wizard so painter can edit.
  // All other states show a read-only status card + timeline.
  if (app.status !== "draft" && app.status !== "info_requested") {
    return <ApplicationStatusCard app={app} />;
  }

  const percent = app.progress?.percent ?? 0;

  return (
    <div className="min-h-screen bg-brand-cream py-6 px-4 sm:px-6" data-testid="membership-apply-page">
      <div className="max-w-3xl mx-auto">
        <header className="mb-6">
          <div className="text-[11px] uppercase tracking-widest text-brand-teal font-semibold">ColourMatch Pro · Membership Application</div>
          <h1 className="text-2xl sm:text-3xl font-bold text-brand-navy mt-1" data-testid="apply-heading">Join ColourMatch Pro</h1>
          <p className="text-sm text-gray-700 mt-1">
            Complete each step below. Your progress saves automatically — you can leave and come back at any time.
          </p>
        </header>

        <ProgressBar step={step} total={TOTAL_STEPS} percent={percent} completedSteps={app.progress?.completed_steps || []} onJump={goToStep} />

        {app.status === "info_requested" && (
          <InfoRequestedBanner app={app} />
        )}

        {err && (
          <div className="mt-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="apply-error-banner">
            {err}
          </div>
        )}

        {submitErrors.length > 0 && (
          <div className="mt-4 p-4 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-sm" data-testid="apply-submit-errors">
            <div className="font-semibold mb-2">Please fix the following before submitting:</div>
            <ul className="list-disc pl-5 space-y-1">
              {submitErrors.map((se) => (
                <li key={se.step}>
                  <button className="underline" onClick={() => setStep(se.step)}>Step {se.step} — {se.label}</button>
                  <ul className="list-disc pl-6 mt-1">{se.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
                </li>
              ))}
            </ul>
          </div>
        )}

        <section className="mt-6 bg-white border border-brand-border rounded-2xl p-5 sm:p-8 shadow-sm" data-testid={`apply-step-${step}`}>
          <div className="text-xs uppercase tracking-widest text-brand-teal font-semibold">Step {step} of {TOTAL_STEPS}</div>
          <h2 className="text-xl sm:text-2xl font-bold text-brand-navy mt-1 mb-6">{STEP_TITLES[step - 1]}</h2>

          {step === 1 && <Step1Business app={app} save={save} debouncedSave={debouncedSave} />}
          {step === 2 && <Step2Compliance app={app} save={save} />}
          {step === 3 && <Step3Service app={app} save={save} debouncedSave={debouncedSave} />}
          {step === 4 && <Step4Profile app={app} save={save} debouncedSave={debouncedSave} />}
          {step === 5 && <Step5References app={app} save={save} />}
          {step === 6 && <Step6Conduct app={app} save={save} debouncedSave={debouncedSave} />}
          {step === 7 && <Step7Tier app={app} save={save} onSubmit={submit} submitting={saving} />}
        </section>

        <nav className="mt-4 flex flex-col-reverse sm:flex-row items-stretch sm:items-center gap-3 justify-between">
          <button
            className="px-4 py-2 rounded-lg border border-brand-navy text-brand-navy font-semibold text-sm hover:bg-brand-cream disabled:opacity-40"
            disabled={step === 1 || saving}
            onClick={() => goToStep(step - 1)}
            data-testid="apply-back-btn"
          >
            ← Back
          </button>
          <div className="text-xs text-gray-500 text-center flex-1" data-testid="apply-autosave-status">
            {saving ? "Saving…" : "Autosaved · you can leave anytime"}
          </div>
          {step < TOTAL_STEPS ? (
            <button
              className="px-4 py-2 rounded-lg bg-brand-navy text-white font-semibold text-sm hover:bg-brand-navy/90 disabled:opacity-40"
              disabled={saving}
              onClick={() => goToStep(step + 1)}
              data-testid="apply-next-btn"
            >
              Continue →
            </button>
          ) : (
            <button
              className="px-4 py-2 rounded-lg bg-brand-teal text-white font-semibold text-sm hover:bg-brand-teal/90 disabled:opacity-50"
              disabled={saving}
              onClick={submit}
              data-testid="apply-submit-btn"
            >
              {saving ? "Submitting…" : "Submit application"}
            </button>
          )}
        </nav>
      </div>
    </div>
  );
}

/* ─── Progress ────────────────────────────────────────────────── */
function ProgressBar({ step, total, percent, completedSteps, onJump }) {
  return (
    <div data-testid="apply-progress">
      <div className="flex items-center justify-between text-xs text-brand-navy font-semibold mb-2">
        <span data-testid="progress-step-label">Step {step} of {total}</span>
        <span data-testid="progress-percent">{percent}% complete</span>
      </div>
      <div className="h-2 bg-white border border-brand-border rounded-full overflow-hidden">
        <div className="h-full bg-brand-teal transition-all" style={{ width: `${percent}%` }} />
      </div>
      <div className="flex gap-1 mt-3 overflow-x-auto pb-1">
        {STEP_TITLES.map((label, i) => {
          const n = i + 1;
          const isDone = completedSteps.includes(n);
          const isCurrent = n === step;
          return (
            <button
              key={n}
              onClick={() => onJump(n)}
              className={`flex-shrink-0 text-[10px] sm:text-xs px-2 py-1 rounded-full font-semibold border transition-all whitespace-nowrap ${
                isCurrent
                  ? "bg-brand-navy text-white border-brand-navy"
                  : isDone
                  ? "bg-brand-teal/15 text-brand-teal border-brand-teal/40"
                  : "bg-white text-gray-500 border-brand-border hover:bg-brand-cream"
              }`}
              data-testid={`progress-chip-${n}`}
            >
              {n}. {label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ─── Common atomic form pieces ──────────────────────────────── */
const inp = "w-full px-3 py-2 border border-brand-border rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-teal/30";
const lbl = "block text-xs font-semibold uppercase tracking-widest text-brand-navy mb-1";

function TextField({ label, value, onChange, placeholder, testid, type = "text", ...rest }) {
  return (
    <label className="block">
      <span className={lbl}>{label}</span>
      <input type={type} className={inp} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
             placeholder={placeholder} data-testid={testid} {...rest} />
    </label>
  );
}
function TextArea({ label, value, onChange, placeholder, testid, rows = 4 }) {
  return (
    <label className="block">
      <span className={lbl}>{label}</span>
      <textarea rows={rows} className={inp} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder} data-testid={testid} />
    </label>
  );
}
function SelectField({ label, value, onChange, options, testid, placeholder = "Select…" }) {
  return (
    <label className="block">
      <span className={lbl}>{label}</span>
      <select className={inp} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
        <option value="">{placeholder}</option>
        {options.map((o) => (
          typeof o === "string"
            ? <option key={o} value={o}>{o}</option>
            : <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}

function FileUpload({ slot, currentUrl, onUploaded, testid }) {
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");
  const handle = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    setErr("");
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post(`/membership/application/me/upload?slot=${slot}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onUploaded(data.url);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };
  return (
    <div>
      <input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" onChange={handle} disabled={uploading} className="hidden" id={testid} data-testid={testid} />
      <label htmlFor={testid} className={`inline-block cursor-pointer text-xs px-3 py-1.5 rounded-full font-semibold border ${uploading ? "opacity-50" : "bg-white border-brand-teal text-brand-teal hover:bg-brand-teal hover:text-white"}`}>
        {uploading ? "Uploading…" : currentUrl ? "Replace file" : "Upload file"}
      </label>
      {currentUrl && <a href={currentUrl} target="_blank" rel="noreferrer" className="ml-2 text-xs text-brand-teal hover:underline">View current</a>}
      {err && <div className="text-xs text-red-700 mt-1">{err}</div>}
    </div>
  );
}

/* ─── Step 1 · Business identity ─────────────────────────────── */
function Step1Business({ app, save, debouncedSave }) {
  const b = app.business || {};
  const patch = (field, v) => {
    const next = { business: { ...b, [field]: v } };
    debouncedSave(next);
  };
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      <TextField label="Legal business name" value={b.legal_name} onChange={(v) => patch("legal_name", v)} testid="biz-legal-name" />
      <TextField label="Trading name (if different)" value={b.trading_name} onChange={(v) => patch("trading_name", v)} testid="biz-trading-name" />
      <TextField label="ABN (11 digits)" value={b.abn} onChange={(v) => patch("abn", v.replace(/[^\d ]/g, ""))} placeholder="12 345 678 901" testid="biz-abn" />
      <SelectField label="Business type" value={b.business_type} options={BUSINESS_TYPES} onChange={(v) => patch("business_type", v)} testid="biz-type" />
      <TextField label="Registered address" value={b.address_line1} onChange={(v) => patch("address_line1", v)} testid="biz-address" />
      <TextField label="Suburb" value={b.suburb} onChange={(v) => patch("suburb", v)} testid="biz-suburb" />
      <SelectField label="State" value={b.state} options={AU_STATES} onChange={(v) => patch("state", v)} testid="biz-state" />
      <TextField label="Postcode" value={b.postcode} onChange={(v) => patch("postcode", v.replace(/\D/g, "").slice(0, 4))} placeholder="e.g. 2000" testid="biz-postcode" />
      <TextField label="Business phone" value={b.phone} onChange={(v) => patch("phone", v)} placeholder="+61 400 000 000" testid="biz-phone" type="tel" />
      <TextField label="Business email" value={b.email} onChange={(v) => patch("email", v)} placeholder="hello@yourpaintco.com" testid="biz-email" type="email" />
      <TextField label="Year established (optional)" value={b.year_established || ""} onChange={(v) => patch("year_established", v ? parseInt(v, 10) : null)} placeholder="e.g. 2015" testid="biz-year" type="number" />
    </div>
  );
}

/* ─── Step 2 · Compliance ────────────────────────────────────── */
function Step2Compliance({ app, save }) {
  const licences = app.licences || [];
  const insurances = app.insurances || [];

  const updateLicence = (idx, patch) => {
    const next = [...licences];
    next[idx] = { ...next[idx], ...patch };
    save({ licences: next }, { silent: true });
  };
  const addLicence = () => save({ licences: [...licences, { number: "", state: "", expiry: "", file_url: "" }] });
  const removeLicence = (idx) => save({ licences: licences.filter((_, i) => i !== idx) });

  const updateInsurance = (idx, patch) => {
    const next = [...insurances];
    next[idx] = { ...next[idx], ...patch };
    save({ insurances: next }, { silent: true });
  };
  const addInsurance = () =>
    save({ insurances: [...insurances, { type: "public_liability", insurer: "", policy_number: "", coverage_amount: null, expiry: "", file_url: "" }] });
  const removeInsurance = (idx) => save({ insurances: insurances.filter((_, i) => i !== idx) });

  return (
    <div className="space-y-6" data-testid="compliance-step">
      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-sm font-bold text-brand-navy">Painting licence(s)</h3>
            <p className="text-xs text-gray-600">Add every state trade licence you hold. You can add more than one.</p>
          </div>
          <button onClick={addLicence} className="text-xs bg-brand-teal text-white px-3 py-1.5 rounded-full font-semibold hover:bg-brand-teal/90" data-testid="add-licence-btn">+ Add licence</button>
        </div>
        {licences.length === 0 && <div className="text-sm text-gray-500 italic p-3 bg-brand-cream rounded-lg" data-testid="licences-empty">No licences added yet. Click <em>+ Add licence</em>.</div>}
        {licences.map((lic, i) => (
          <div key={lic.id || i} className="grid sm:grid-cols-2 gap-3 border border-brand-border p-3 rounded-lg mb-3" data-testid={`licence-row-${i}`}>
            <TextField label="Licence number" value={lic.number} onChange={(v) => updateLicence(i, { number: v })} testid={`licence-num-${i}`} />
            <SelectField label="Issuing state" value={lic.state} options={AU_STATES} onChange={(v) => updateLicence(i, { state: v })} testid={`licence-state-${i}`} />
            <TextField label="Expiry date" value={lic.expiry} onChange={(v) => updateLicence(i, { expiry: v })} placeholder="YYYY-MM-DD" testid={`licence-expiry-${i}`} type="date" />
            <TextField label="Certificate URL (or upload below)" value={lic.file_url} onChange={(v) => updateLicence(i, { file_url: v })} placeholder="Link to scan or leave blank" testid={`licence-url-${i}`} />
            <div className="sm:col-span-2">
              <FileUpload slot="licence" currentUrl={lic.file_url} onUploaded={(url) => updateLicence(i, { file_url: url })} testid={`licence-upload-${i}`} />
            </div>
            <button onClick={() => removeLicence(i)} className="text-xs text-red-700 hover:underline sm:col-span-2 text-left" data-testid={`licence-remove-${i}`}>Remove this licence</button>
          </div>
        ))}
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-sm font-bold text-brand-navy">Insurance policies</h3>
            <p className="text-xs text-gray-600">Public liability is required. Add workers&apos; comp if you have employees.</p>
          </div>
          <button onClick={addInsurance} className="text-xs bg-brand-teal text-white px-3 py-1.5 rounded-full font-semibold hover:bg-brand-teal/90" data-testid="add-insurance-btn">+ Add insurance</button>
        </div>
        {insurances.length === 0 && <div className="text-sm text-gray-500 italic p-3 bg-brand-cream rounded-lg" data-testid="insurances-empty">No insurance policies added yet.</div>}
        {insurances.map((ins, i) => (
          <div key={ins.id || i} className="grid sm:grid-cols-2 gap-3 border border-brand-border p-3 rounded-lg mb-3" data-testid={`insurance-row-${i}`}>
            <SelectField label="Type" value={ins.type} options={[{ value: "public_liability", label: "Public liability" }, { value: "workers_comp", label: "Workers' compensation" }, { value: "other", label: "Other" }]} onChange={(v) => updateInsurance(i, { type: v })} testid={`insurance-type-${i}`} />
            <TextField label="Insurer" value={ins.insurer} onChange={(v) => updateInsurance(i, { insurer: v })} testid={`insurance-insurer-${i}`} />
            <TextField label="Policy number" value={ins.policy_number} onChange={(v) => updateInsurance(i, { policy_number: v })} testid={`insurance-policy-${i}`} />
            <TextField label="Coverage (AUD)" value={ins.coverage_amount || ""} onChange={(v) => updateInsurance(i, { coverage_amount: v ? parseFloat(v) : null })} placeholder="e.g. 5000000" testid={`insurance-cover-${i}`} type="number" />
            <TextField label="Expiry date" value={ins.expiry} onChange={(v) => updateInsurance(i, { expiry: v })} testid={`insurance-expiry-${i}`} type="date" />
            <TextField label="Certificate URL (or upload below)" value={ins.file_url} onChange={(v) => updateInsurance(i, { file_url: v })} testid={`insurance-url-${i}`} />
            <div className="sm:col-span-2">
              <FileUpload slot="insurance" currentUrl={ins.file_url} onUploaded={(url) => updateInsurance(i, { file_url: url })} testid={`insurance-upload-${i}`} />
            </div>
            <button onClick={() => removeInsurance(i)} className="text-xs text-red-700 hover:underline sm:col-span-2 text-left" data-testid={`insurance-remove-${i}`}>Remove this policy</button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Step 3 · Service coverage ──────────────────────────────── */
function Step3Service({ app, save, debouncedSave }) {
  const s = app.service || {};
  const patchService = (field, v) => debouncedSave({ service: { ...s, [field]: v } });
  const togglePostcode = (v) => {
    const pc = v.trim();
    if (!pc || !/^\d{4}$/.test(pc)) return;
    const cur = s.postcodes || [];
    if (cur.includes(pc)) return;
    save({ service: { ...s, postcodes: [...cur, pc] } }, { silent: true });
  };
  const removePostcode = (pc) => save({ service: { ...s, postcodes: (s.postcodes || []).filter((p) => p !== pc) } }, { silent: true });
  const toggleJob = (job) => {
    const cur = s.job_types || [];
    const next = cur.includes(job) ? cur.filter((j) => j !== job) : [...cur, job];
    save({ service: { ...s, job_types: next } }, { silent: true });
  };
  const [pcInput, setPcInput] = useState("");

  return (
    <div className="space-y-5" data-testid="service-step">
      <SelectField label="Primary service state" value={s.primary_state} options={AU_STATES} onChange={(v) => patchService("primary_state", v)} testid="svc-primary-state" />
      <div>
        <span className={lbl}>Service postcodes</span>
        <div className="flex gap-2 mb-2">
          <input
            className={inp}
            value={pcInput}
            onChange={(e) => setPcInput(e.target.value.replace(/\D/g, "").slice(0, 4))}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); togglePostcode(pcInput); setPcInput(""); } }}
            placeholder="Type postcode and press Enter (e.g. 2000)"
            data-testid="svc-postcode-input"
          />
          <button onClick={() => { togglePostcode(pcInput); setPcInput(""); }} className="px-3 py-2 rounded-lg bg-brand-navy text-white font-semibold text-sm" data-testid="svc-postcode-add">Add</button>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="svc-postcode-list">
          {(s.postcodes || []).map((pc) => (
            <span key={pc} className="inline-flex items-center gap-1 bg-brand-teal/15 text-brand-teal font-semibold px-2.5 py-1 rounded-full text-xs" data-testid={`svc-postcode-${pc}`}>
              {pc}
              <button onClick={() => removePostcode(pc)} className="text-brand-teal hover:text-red-600" aria-label={`Remove ${pc}`}>×</button>
            </span>
          ))}
          {(s.postcodes || []).length === 0 && <span className="text-xs text-gray-500 italic">No postcodes added yet.</span>}
        </div>
      </div>
      <div>
        <span className={lbl}>Job types</span>
        <div className="flex flex-wrap gap-2">
          {JOB_TYPES.map((jt) => {
            const active = (s.job_types || []).includes(jt);
            return (
              <button
                key={jt}
                onClick={() => toggleJob(jt)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold capitalize border ${active ? "bg-brand-navy text-white border-brand-navy" : "bg-white text-brand-navy border-brand-border hover:bg-brand-cream"}`}
                data-testid={`svc-job-${jt}`}
              >
                {jt}
              </button>
            );
          })}
        </div>
      </div>
      <TextField label="Minimum job size (AUD, optional)" value={s.minimum_job_size_aud || ""} onChange={(v) => patchService("minimum_job_size_aud", v ? parseFloat(v) : null)} placeholder="e.g. 2000" testid="svc-min-job" type="number" />
    </div>
  );
}

/* ─── Step 4 · Profile & gallery ─────────────────────────────── */
function Step4Profile({ app, save, debouncedSave }) {
  const p = app.profile || {};
  const gallery = app.gallery || [];
  const patchProfile = (field, v) => debouncedSave({ profile: { ...p, [field]: v } });

  const addPhoto = () =>
    save({ gallery: [...gallery, { url: "", title: "", surface_tag: "interior", is_before_after: false, sort_order: gallery.length }] });
  const updatePhoto = (idx, patch) => {
    const next = [...gallery];
    next[idx] = { ...next[idx], ...patch };
    save({ gallery: next }, { silent: true });
  };
  const removePhoto = (idx) => save({ gallery: gallery.filter((_, i) => i !== idx) });

  return (
    <div className="space-y-5" data-testid="profile-step">
      <TextField label="Public profile slug (optional)" value={p.slug} onChange={(v) => patchProfile("slug", v.toLowerCase().replace(/[^a-z0-9-]/g, "-"))} placeholder="e.g. north-shore-painters" testid="profile-slug" />
      <TextField label="Tagline" value={p.tagline} onChange={(v) => patchProfile("tagline", v)} placeholder="Interior specialists · Sydney North Shore" testid="profile-tagline" />
      <TextArea label="About us (min 40 characters)" value={p.about} onChange={(v) => patchProfile("about", v)} placeholder="Tell homeowners about your business, experience, and what you're known for." testid="profile-about" rows={4} />
      <TextField label="Founder photo URL (optional)" value={p.founder_photo_url} onChange={(v) => patchProfile("founder_photo_url", v)} placeholder="Link to a portrait photo" testid="profile-founder-photo" />
      <TextField label="Team size (optional)" value={p.team_size || ""} onChange={(v) => patchProfile("team_size", v ? parseInt(v, 10) : null)} placeholder="e.g. 4" testid="profile-team-size" type="number" />

      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-sm font-bold text-brand-navy">Gallery (min 6 photos)</h3>
            <p className="text-xs text-gray-600">Paste image URLs of your best work. You can also upload files after approval.</p>
          </div>
          <button onClick={addPhoto} className="text-xs bg-brand-teal text-white px-3 py-1.5 rounded-full font-semibold hover:bg-brand-teal/90" data-testid="add-photo-btn">+ Add photo</button>
        </div>
        {gallery.length === 0 && <div className="text-sm text-gray-500 italic p-3 bg-brand-cream rounded-lg" data-testid="gallery-empty">No photos yet.</div>}
        {gallery.map((g, i) => (
          <div key={g.id || i} className="grid sm:grid-cols-3 gap-3 border border-brand-border p-3 rounded-lg mb-3" data-testid={`gallery-row-${i}`}>
            <TextField label={`Photo ${i + 1} URL (or upload)`} value={g.url} onChange={(v) => updatePhoto(i, { url: v })} placeholder="https://…" testid={`gallery-url-${i}`} />
            <TextField label="Title" value={g.title} onChange={(v) => updatePhoto(i, { title: v })} testid={`gallery-title-${i}`} />
            <SelectField label="Surface" value={g.surface_tag} options={["interior", "exterior"]} onChange={(v) => updatePhoto(i, { surface_tag: v })} testid={`gallery-surface-${i}`} />
            <div className="sm:col-span-3">
              <FileUpload slot="gallery" currentUrl={g.url} onUploaded={(url) => updatePhoto(i, { url })} testid={`gallery-upload-${i}`} />
            </div>
            <label className="flex items-center gap-2 text-xs sm:col-span-2">
              <input type="checkbox" checked={!!g.is_before_after} onChange={(e) => updatePhoto(i, { is_before_after: e.target.checked })} data-testid={`gallery-ba-${i}`} />
              Before-and-after pair
            </label>
            <button onClick={() => removePhoto(i)} className="text-xs text-red-700 hover:underline text-right" data-testid={`gallery-remove-${i}`}>Remove</button>
            {g.url && <img src={g.url} alt="" className="sm:col-span-3 rounded-lg h-32 object-cover border border-brand-border" onError={(e) => e.target.style.display = "none"} />}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Step 5 · References ────────────────────────────────────── */
function Step5References({ app, save }) {
  const refs = app.references || [];
  const add = () => save({ references: [...refs, { name: "", suburb: "", photo_url: "", testimonial: "", consent_confirmed: false }] });
  const update = (i, patch) => {
    const next = [...refs];
    next[i] = { ...next[i], ...patch };
    save({ references: next }, { silent: true });
  };
  const remove = (i) => save({ references: refs.filter((_, idx) => idx !== i) });

  return (
    <div className="space-y-4" data-testid="refs-step">
      <div className="p-3 rounded-lg bg-brand-cream text-xs text-gray-700">
        References are optional but weighted in your approval review. You may add up to 3. Only include a reference if you have written consent from them.
      </div>
      {refs.length === 0 && (
        <div className="text-sm text-gray-500 italic" data-testid="refs-empty">No references yet — this step is optional.</div>
      )}
      {refs.map((r, i) => (
        <div key={r.id || i} className="border border-brand-border p-3 rounded-lg grid sm:grid-cols-2 gap-3" data-testid={`ref-row-${i}`}>
          <TextField label="Homeowner name" value={r.name} onChange={(v) => update(i, { name: v })} testid={`ref-name-${i}`} />
          <TextField label="Suburb" value={r.suburb} onChange={(v) => update(i, { suburb: v })} testid={`ref-suburb-${i}`} />
          <TextField label="Project photo URL (optional)" value={r.photo_url} onChange={(v) => update(i, { photo_url: v })} testid={`ref-photo-${i}`} />
          <TextArea label="One-line testimonial" value={r.testimonial} onChange={(v) => update(i, { testimonial: v })} testid={`ref-test-${i}`} rows={2} />
          <label className="flex items-start gap-2 text-xs sm:col-span-2">
            <input type="checkbox" checked={!!r.consent_confirmed} onChange={(e) => update(i, { consent_confirmed: e.target.checked })} data-testid={`ref-consent-${i}`} className="mt-0.5" />
            <span>I confirm this homeowner has given written consent for their name, suburb, photo and testimonial to be displayed on my ColourMatch profile.</span>
          </label>
          <button onClick={() => remove(i)} className="text-xs text-red-700 hover:underline text-left" data-testid={`ref-remove-${i}`}>Remove reference</button>
        </div>
      ))}
      {refs.length < 3 && (
        <button onClick={add} className="text-xs bg-brand-teal text-white px-3 py-1.5 rounded-full font-semibold hover:bg-brand-teal/90" data-testid="add-ref-btn">+ Add reference</button>
      )}
    </div>
  );
}

/* ─── Step 6 · Code of Conduct ───────────────────────────────── */
const CODE_OF_CONDUCT = [
  { title: "Professionalism", body: "I show up on time. I dress and communicate as a professional. I respect the homeowner's home like it is my own." },
  { title: "Respect", body: "I treat every homeowner, coworker, and fellow ColourMatch member with dignity, regardless of background, gender, race, or belief. No exceptions." },
  { title: "Communication", body: "I respond to homeowner enquiries within 2 business days. If I cannot take a job, I say so clearly and quickly — silence is worse than a no." },
  { title: "Honesty", body: "I quote what I can deliver. I disclose surcharges before the job starts, not after. I never charge for work I have not done. I never use another painter's photos as my own." },
  { title: "Quality workmanship & consumer law compliance", body: "I comply with all applicable Australian Consumer Law obligations and honour any written workmanship warranty or guarantee I provide to the homeowner. Nothing in this Code limits a homeowner's statutory consumer rights." },
  { title: "Customer care", body: "I address concerns raised during or after the job with genuine effort. I do not walk away from mistakes." },
  { title: "The trade above the transaction", body: "I never disparage another painter to win a job. I never poach a homeowner's active engagement with another ColourMatch painter." },
];

function Step6Conduct({ app, save, debouncedSave }) {
  const c = app.conduct_acceptance || {};
  const patchConduct = (field, v) => debouncedSave({ conduct_acceptance: { ...c, [field]: v } });
  return (
    <div className="space-y-4" data-testid="conduct-step">
      <div className="border border-brand-border rounded-xl p-4 bg-brand-cream/40 space-y-3">
        {CODE_OF_CONDUCT.map((item, i) => (
          <div key={i} data-testid={`conduct-promise-${i + 1}`}>
            <div className="font-bold text-brand-navy text-sm">{i + 1}. {item.title}</div>
            <p className="text-xs text-gray-700 mt-0.5">{item.body}</p>
          </div>
        ))}
      </div>
      <label className="flex items-start gap-2 text-sm">
        <input type="checkbox" checked={!!c.accepted} onChange={(e) => save({ conduct_acceptance: { ...c, accepted: e.target.checked, signed_at: e.target.checked ? new Date().toISOString() : null } })} data-testid="conduct-accept" className="mt-1" />
        <span>I agree to the ColourMatch Pro Code of Conduct above.</span>
      </label>
      <label className="flex items-start gap-2 text-sm">
        <input type="checkbox" checked={!!c.terms_accepted} onChange={(e) => save({ conduct_acceptance: { ...c, terms_accepted: e.target.checked } })} data-testid="conduct-terms" className="mt-1" />
        <span>I have read and accept the Membership Terms.</span>
      </label>
      <TextField label="Type your full legal name to sign" value={c.signature_name} onChange={(v) => patchConduct("signature_name", v)} placeholder="Full name as it appears on your licence" testid="conduct-signature" />
      <p className="text-xs text-gray-500">By signing above you confirm your intent to be bound by the Code of Conduct. Your IP address and signing time are recorded for audit.</p>
    </div>
  );
}

/* ─── Step 7 · Tier selection ────────────────────────────────── */
function Step7Tier({ app, save, onSubmit, submitting }) {
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const t = app.tier_selection || {};
  useEffect(() => {
    api.get("/membership/tiers").then((r) => setTiers(r.data.tiers || [])).finally(() => setLoading(false));
  }, []);
  const choose = (slug) => save({ tier_selection: { ...t, tier_slug: slug } }, { silent: true });
  const cycle = (bc) => save({ tier_selection: { ...t, billing_cycle: bc } }, { silent: true });

  if (loading) return <div>Loading tiers…</div>;
  return (
    <div className="space-y-6" data-testid="tier-step">
      <div className="text-sm text-gray-700">
        Choose a plan. Final pricing will be confirmed by ColourMatch before payment, and shown to you again at activation.
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        {tiers.map((tier) => {
          const selected = t.tier_slug === tier.slug;
          return (
            <button
              key={tier.slug}
              onClick={() => choose(tier.slug)}
              className={`text-left border-2 rounded-2xl p-5 transition-all ${selected ? "border-brand-teal bg-brand-teal/5" : "border-brand-border bg-white hover:border-brand-teal/50"}`}
              data-testid={`tier-card-${tier.slug}`}
            >
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-bold text-brand-navy">{tier.name}</h3>
                {selected && <span className="text-xs bg-brand-teal text-white px-2 py-0.5 rounded-full font-semibold">Selected</span>}
              </div>
              <p className="text-xs text-gray-600 mb-3">{tier.positioning}</p>
              <div className="text-brand-navy font-bold mb-3">
                <span data-testid={`tier-price-${tier.slug}`}>Pricing: TBC</span>
                <span className="text-xs text-gray-500 font-normal ml-1">(placeholder — final price confirmed before payment)</span>
              </div>
              <ul className="text-xs text-gray-700 space-y-1 list-disc pl-4">
                {(tier.features_confirmed || []).map((f, i) => <li key={i}>{f}</li>)}
              </ul>
            </button>
          );
        })}
      </div>
      <div>
        <div className={lbl}>Billing cycle</div>
        <div className="flex gap-2">
          {["monthly", "annual"].map((bc) => (
            <button
              key={bc}
              onClick={() => cycle(bc)}
              className={`px-4 py-2 rounded-full text-xs font-semibold capitalize border ${t.billing_cycle === bc ? "bg-brand-navy text-white border-brand-navy" : "bg-white text-brand-navy border-brand-border hover:bg-brand-cream"}`}
              data-testid={`billing-${bc}`}
            >
              {bc}
            </button>
          ))}
        </div>
      </div>
      <div className="p-3 rounded-lg bg-brand-cream text-xs text-gray-700 border border-brand-border">
        <strong>What happens next?</strong> After you submit, our team will review your application (target: 5 business days). Once approved, you&apos;ll receive an email to activate your membership by paying the first invoice. Your data is preserved forever — no matter what happens with your subscription.
      </div>
    </div>
  );
}

/* ─── Status card (for submitted / decided applications) ─────── */
function ApplicationStatusCard({ app }) {
  const [timeline, setTimeline] = useState(null);
  useEffect(() => {
    api.get("/membership/application/me/timeline").then((r) => setTimeline(r.data)).catch(() => {});
  }, []);

  const labels = {
    submitted: { title: "Application submitted", tone: "teal", body: "We've received your application and it's in the review queue. We'll email you within a few business days." },
    info_requested: { title: "More information requested", tone: "amber", body: (app.review_history || []).slice().reverse().find((h) => h.action === "request_info")?.applicant_message || "Our team has asked for a small update. Check your email — then log in and update the sections we flagged." },
    approved: { title: "Application approved", tone: "success", body: "Welcome to ColourMatch Pro! Activation payment will open when our payment system is live." },
    approved_with_conditions: { title: "Approved with conditions", tone: "success", body: (app.conditions || "See email for conditions.") },
    rejected: { title: "Application not approved", tone: "muted", body: "Please see your email for details. You may be able to reapply." },
  };
  const s = labels[app.status] || { title: app.status, tone: "muted", body: "" };
  const toneClass = {
    teal: "bg-brand-teal/10 border-brand-teal/40",
    amber: "bg-amber-50 border-amber-200",
    success: "bg-brand-success/10 border-brand-success/40",
    muted: "bg-gray-100 border-gray-200",
  }[s.tone];
  const canResume = app.status === "info_requested";
  return (
    <div className="min-h-[60vh] py-8 px-4 sm:px-6 bg-brand-cream" data-testid={`app-status-${app.status}`}>
      <div className="max-w-2xl mx-auto">
        <div className={`border rounded-2xl p-6 sm:p-8 text-center ${toneClass}`}>
          <div className="text-xs uppercase tracking-widest text-brand-teal font-semibold mb-2">Application status</div>
          <h1 className="text-2xl sm:text-3xl font-bold text-brand-navy mb-3">{s.title}</h1>
          <p className="text-sm text-gray-700 whitespace-pre-wrap">{s.body}</p>
          {canResume && (
            <button
              onClick={() => window.location.reload()}
              className="mt-5 px-4 py-2 rounded-lg bg-brand-navy text-white font-semibold text-sm hover:bg-brand-navy/90"
              data-testid="app-resume-btn"
            >
              Update my application
            </button>
          )}
        </div>

        {/* Timeline */}
        {timeline?.stages && (
          <div className="mt-8" data-testid="applicant-timeline">
            <div className="text-xs uppercase tracking-widest text-brand-teal font-semibold mb-3">Your journey</div>
            <ol className="space-y-3">
              {timeline.stages.map((stage) => (
                <li key={stage.key} className={`flex items-start gap-3 p-3 rounded-lg border transition-all ${
                  stage.state === "completed" ? "bg-brand-success/10 border-brand-success/30" :
                  stage.state === "current" ? "bg-brand-teal/10 border-brand-teal/40 ring-2 ring-brand-teal/20" :
                  "bg-white border-brand-border"
                }`} data-testid={`timeline-stage-${stage.key}`}>
                  <span className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                    stage.state === "completed" ? "bg-brand-success text-white" :
                    stage.state === "current" ? "bg-brand-teal text-white" :
                    "bg-white border border-brand-border text-gray-400"
                  }`}>
                    {stage.state === "completed" ? "✓" : stage.state === "current" ? "•" : "○"}
                  </span>
                  <div className="flex-1 text-left">
                    <div className={`font-semibold text-sm ${stage.state === "upcoming" ? "text-gray-400" : "text-brand-navy"}`} data-testid={`timeline-label-${stage.key}`}>{stage.label}</div>
                    <div className={`text-xs ${stage.state === "upcoming" ? "text-gray-400" : "text-gray-600"}`}>{stage.sub}</div>
                    {stage.state === "current" && <div className="mt-1 inline-block text-[10px] uppercase tracking-widest font-bold text-brand-teal">Current</div>}
                  </div>
                </li>
              ))}
            </ol>
          </div>
        )}

        {/* Latest admin note if info_requested */}
        {app.status === "info_requested" && (
          <AdminNote history={app.review_history} />
        )}
      </div>
    </div>
  );
}

function AdminNote({ history = [] }) {
  const last = [...history].reverse().find((h) => h.action === "request_info");
  if (!last) return null;
  return (
    <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg" data-testid="admin-note">
      <div className="text-xs uppercase tracking-widest text-amber-700 font-semibold mb-1">From our team</div>
      {last.applicant_message && <p className="text-sm text-amber-900 whitespace-pre-wrap">{last.applicant_message}</p>}
      {last.reason && !last.applicant_message && <p className="text-sm text-amber-900">{last.reason}</p>}
      <div className="text-[10px] text-amber-700 mt-2">Sent {new Date(last.at).toLocaleString("en-AU")}</div>
    </div>
  );
}

function InfoRequestedBanner({ app }) {
  const last = [...(app.review_history || [])].reverse().find((h) => h.action === "request_info");
  const fields = app.info_fields || [];
  return (
    <div className="mt-4 p-4 rounded-xl bg-amber-50 border border-amber-200" data-testid="info-requested-banner">
      <div className="text-xs uppercase tracking-widest text-amber-800 font-semibold mb-2">More information requested</div>
      {last?.applicant_message && (
        <p className="text-sm text-amber-900 whitespace-pre-wrap mb-2">{last.applicant_message}</p>
      )}
      {fields.length > 0 && (
        <div className="text-xs text-amber-900">
          <strong>Sections to update:</strong> {fields.map((f) => f.replace(/_/g, " ")).join(", ")}
        </div>
      )}
      <p className="text-xs text-amber-800 mt-2">Update the sections above and click <strong>Submit application</strong> at the end to send it back for review.</p>
    </div>
  );
}
